import { useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import isEqual from 'lodash/isEqual'

import {
  selectCloneLeaguesSession,
  selectCloneLeaguesSessionID,
  selectCloneLeaguesSessionData,
  selectSessionSaverLoading,
  selectSessionSaverError,
  updateLeagueConfig,
  updateAllLeaguesConfigs,
  stripAllLeagueNames,
  syncSelectedLeaguesToSession,
  syncEnhancedLeaguesToSession,
  saveSession,
} from '../reducers/session_saver'
import { selectSelectedLeaguesForCloning, setSelectedLeaguesForCloning } from '../reducers/selected_leagues_for_cloning'
import { fetchPotentialLeaguesForCloningMergedParams } from '../reducers/potential_leagues_for_cloning'
import { selectCloningStep, applyOutdatedLeaguesResponse, setCloningModal, setSessionStarting } from '../reducers/cloning_steps'
import { selectProductType, selectTextSearch } from '../reducers/leagues_filter_values'
import {
  selectConfigsOptions,
  selectConfigsSeason,
  setConfigsOptions,
  getInitialState as getInitialStateLeaguesConfigs,
  OPTION_CAPABILITY_FLAGS,
} from '../reducers/leagues_configs'
import { selectEnhancedLeagues } from '../reducers/enhanced_leagues'
import {
  translateTimeSeries,
  calculateFutureEquivalentDate,
  parseDateUTC,
  todayUTC,
  toISODateString,
} from '../timeUtils'
import { ALL_ENTRIES_FILTER_VALUE, CloningStepsEnum, CloningStepsModals } from '../constants'
import { startCloneLeaguesSession } from '../reducers/clone_league_session_starter'

// Thunk because it needs both dispatch (to fan out three coordinated actions) and getState
// (to read the live selected-leagues list rather than a stale closure value).
// Defined at module level — it depends only on imported selectors and action creators,
// not on any hook state — so it does not need to live inside useSessionSaver or be
// wrapped in useCallback.
const handleOutdatedLeaguesResult = (result) => (dispatch, getState) => {
  if (!result?.data?.outdatedLeagues?.length) return
  const outdatedIds = new Set(result.data.outdatedLeagues.map(l => String(l.id)))
  const currentSelected = selectSelectedLeaguesForCloning(getState())
  dispatch(setSelectedLeaguesForCloning(currentSelected.filter(l => !outdatedIds.has(String(l.id)))))
  dispatch(fetchPotentialLeaguesForCloningMergedParams())
  dispatch(applyOutdatedLeaguesResponse(result.data))
}

const useSessionSaver = () => {
  const dispatch = useDispatch()

  const session = useSelector(selectCloneLeaguesSession)
  const sessionID = useSelector(selectCloneLeaguesSessionID)
  const sessionData = useSelector(selectCloneLeaguesSessionData, isEqual)
  const isLoading = useSelector(selectSessionSaverLoading)
  const error = useSelector(selectSessionSaverError)
  const enhancedLeagues = useSelector(selectEnhancedLeagues)
  const textSearch = useSelector(selectTextSearch)
  const productType = useSelector(selectProductType)

  const mergedLeagues = useMemo(() => {
    const sessionLeagues = sessionData?.leagues || {}
    const enhancedLeaguesByID = (enhancedLeagues || []).reduce((acc, league) => {
      acc[String(league.id)] = league
      return acc
    }, {})

    return Object.entries(sessionLeagues).reduce((acc, [ id, sessionLeague ]) => {
      acc[id] = {
        ...enhancedLeaguesByID[id],
        ...sessionLeague,
      }
      return acc
    }, {})
  }, [ sessionData, enhancedLeagues ])

  // STEP 1 - Save selected leagues
  // Reads selectedLeagues from the store inside the thunk so the save always uses
  // the latest Redux state rather than a potentially stale closure captured at render time.
  const saveSelectedLeaguesToSession = useCallback(() => {
    dispatch((innerDispatch, getState) => {
      const currentSelectedLeagues = selectSelectedLeaguesForCloning(getState())
      innerDispatch(syncSelectedLeaguesToSession(currentSelectedLeagues))
    })
  }, [ dispatch ])

  // STEP 3 - Save selected configs
  // Reads configsOptions, season, and cloningStep from the store inside the thunk
  // so the save always uses the latest Redux state rather than a potentially stale
  // closure captured at render time.
  const saveConfigsToSession = useCallback(() => {
    dispatch((innerDispatch, getState) => {
      const currentConfigsOptions = selectConfigsOptions(getState())
      const currentSeason = selectConfigsSeason(getState())
      const currentStep = selectCloningStep(getState())
      updateAllLeaguesConfigs({ ...currentConfigsOptions, season: currentSeason?.value })(innerDispatch, getState)
      const updatedSession = selectCloneLeaguesSession(getState())
      innerDispatch(saveSession({
        cloneLeaguesSessionID: updatedSession?.id,
        data: { ...updatedSession?.data, cloningStep: currentStep },
      }))
    })
  }, [ dispatch ])

  const saveSessionToServer = useCallback(() => {
    dispatch((innerDispatch, getState) => {
      const currentSelectedLeagues = selectSelectedLeaguesForCloning(getState())
      const currentConfigsOptions = selectConfigsOptions(getState())
      const currentSeason = selectConfigsSeason(getState())
      const currentStep = selectCloningStep(getState())
      syncSelectedLeaguesToSession(currentSelectedLeagues)(innerDispatch, getState)
      updateAllLeaguesConfigs({ ...currentConfigsOptions, season: currentSeason?.value })(innerDispatch, getState)
      const updatedSession = selectCloneLeaguesSession(getState())
      return innerDispatch(saveSession({
        cloneLeaguesSessionID: updatedSession?.id,
        data: { ...updatedSession?.data, cloningStep: currentStep },
      })).then(result => {
        innerDispatch(handleOutdatedLeaguesResult(result))
      })
    })
  }, [ dispatch ])

  // Step 4 - Leagues for date selection
  const leaguesForDatePicking = useMemo(() => {
    return Object.entries(mergedLeagues).reduce((acc, [ id, league ]) => {
      if (textSearch && !league.name?.toLowerCase().includes(textSearch.toLowerCase())) {
        return acc
      }
      if (productType && productType !== ALL_ENTRIES_FILTER_VALUE && league.product !== productType) {
        return acc
      }
      acc[id] = league
      return acc
    }, {})
  }, [ mergedLeagues, textSearch, productType ])

  const setMassDates = useCallback((leagueID, dates) => {
    dispatch(updateLeagueConfig(leagueID, { mass_dates: dates }))
  }, [ dispatch ])

  const addDateToMassDates = useCallback((leagueID, date) => {
    const currentMassDates = sessionData?.leagues?.[leagueID]?.mass_dates || []
    const leagueRounds = mergedLeagues?.[leagueID]?.rounds || []

    if (currentMassDates.length + 1 > leagueRounds.length) {
      return
    }

    setMassDates(leagueID, [ ...currentMassDates, date ])
  }, [ sessionData, mergedLeagues, setMassDates ])

  const removeDateFromMassDates = useCallback((leagueID, date) => {
    const currentMassDates = sessionData?.leagues?.[leagueID]?.mass_dates || []
    const firstMatch = currentMassDates.indexOf(date)
    if (firstMatch === -1) {
      return
    }
    setMassDates(leagueID, currentMassDates.filter((_, index) => index !== firstMatch))
  }, [ sessionData, setMassDates ])

  const prePopulateMassDates = useCallback(() => {
    const today = todayUTC()

    Object.entries(mergedLeagues).forEach(([ leagueID, league ]) => {
      const rounds = league?.rounds || []
      const currentMassDates = mergedLeagues?.[leagueID]?.mass_dates

      if (!rounds.length || currentMassDates?.length) {
        return
      }

      const roundDates = rounds
        .map(round => round.playAt)
        .sort()

      const firstRoundDate = parseDateUTC(roundDates[0])

      if (firstRoundDate >= today) {
        setMassDates(leagueID, roundDates)
      } else {
        const newStartDate = calculateFutureEquivalentDate(firstRoundDate, today)
        const translatedDates = translateTimeSeries(roundDates, newStartDate)
        const dates = translatedDates.map(d => toISODateString(d))
        setMassDates(leagueID, dates)
      }
    })
  }, [ mergedLeagues, setMassDates ])

  const saveEnhancedLeaguesToSession = useCallback(() => {
    dispatch((innerDispatch, getState) => {
      const currentEnhancedLeagues = selectEnhancedLeagues(getState())
      if (!currentEnhancedLeagues?.length) return
      const currentStep = selectCloningStep(getState())
      syncEnhancedLeaguesToSession(currentEnhancedLeagues)(innerDispatch, getState)
      const updatedSession = selectCloneLeaguesSession(getState())
      innerDispatch(saveSession({
        cloneLeaguesSessionID: updatedSession?.id,
        data: { ...updatedSession?.data, cloningStep: currentStep },
      }))
    })
  }, [ dispatch ])

  // Re-initializes configsOptions once enhanced league data (capability flags, rounds) has loaded.
  // Called after fetchEnhancedLeaguesFromList resolves so capability-gated options reflect real
  // league capabilities rather than the empty-array defaults computed at store creation.
  // Only capability-gated options are updated — non-capability options preserve the user's
  // explicit choices. Within capability-gated options, only those that transition from
  // unavailable (false) to available are set to true; options the user already deselected
  // while they were available are left unchanged.
  const reinitializeConfigsOptions = useCallback(() => {
    if (!enhancedLeagues?.length) return
    const { configsOptions: initializedOptions } = getInitialStateLeaguesConfigs(session, enhancedLeagues, { skipStoredValues: true })
    dispatch((innerDispatch, getState) => {
      const currentOptions = selectConfigsOptions(getState())
      const merged = { ...currentOptions }
      Object.entries(OPTION_CAPABILITY_FLAGS).forEach(([ optionId, flag ]) => {
        const nowAvailable = enhancedLeagues.some(l => l[flag])
        if (nowAvailable && !currentOptions[optionId]) {
          merged[optionId] = initializedOptions[optionId]
        }
      })
      innerDispatch(setConfigsOptions(merged))
    })
  }, [ dispatch, session, enhancedLeagues ])

  const startSessionJobs = useCallback(() => {
    dispatch((innerDispatch, getState) => {
      const currentStep = selectCloningStep(getState())
      if (currentStep === CloningStepsEnum.CONFIG) {
        const currentConfigsOptions = selectConfigsOptions(getState())
        const currentSeason = selectConfigsSeason(getState())
        updateAllLeaguesConfigs({ ...currentConfigsOptions, season: currentSeason?.value })(innerDispatch, getState)
      }
      const updatedSession = selectCloneLeaguesSession(getState())

      // Locks the footer for the duration of the start request. A 202 deliberately leaves
      // it locked: the starter reducer has already assigned window.location.href, and
      // unlocking mid-navigation would let the user back into the wizard while the browser
      // is still loading the dashboard. Every other outcome releases it.
      innerDispatch(setSessionStarting(true))

      return innerDispatch(startCloneLeaguesSession({
        cloneLeaguesSessionID: updatedSession?.id,
        data: { ...updatedSession?.data, cloningStep: currentStep },
      })).then(result => {
        if (result?.status !== 202) {
          innerDispatch(setSessionStarting(false))
        }

        if (result?.status === 409) {
          innerDispatch(setCloningModal(CloningStepsModals.SESSION_IN_PROGRESS))
          return
        }
        innerDispatch(handleOutdatedLeaguesResult(result))
      }).catch(() => {
        // No response at all (network failure, or a body that is not JSON) — still a failed
        // start, so the footer has to be released rather than left locked forever.
        innerDispatch(setSessionStarting(false))
      })
    })
  }, [ dispatch ])

  return {
    session,
    sessionID,
    sessionData,
    isLoading,
    error,
    mergedLeagues,
    leaguesForDatePicking,
    saveSelectedLeaguesToSession,
    saveConfigsToSession,
    updateNameForLeague: (leagueID, newName) => dispatch(updateLeagueConfig(leagueID, { league_name: newName })),
    saveSessionToServer,
    startSessionJobs,
    setMassDates,
    addDateToMassDates,
    removeDateFromMassDates,
    prePopulateMassDates,
    saveEnhancedLeaguesToSession,
    reinitializeConfigsOptions,
    stripAllLeagueNames: () => dispatch(stripAllLeagueNames()),
  }
}

export default useSessionSaver
